/**
 * The Field Force field paths that are visible iff `ff_involvement === true`
 * (R5.2, R5.3).
 *
 * This is the exhaustive list from the design: FF roles, activity types,
 * audiences, has_material, work_order, and information_contribution.
 */
const FIELD_FORCE_FIELD_PATHS = Object.freeze([
    'fieldForceScope.roles',
    'fieldForceScope.activityTypes',
    'fieldForceScope.audiences',
    'fieldForceScope.hasMaterial',
    'fieldForceScope.workOrder',
    'fieldForceScope.informationContribution',
]);
/**
 * The DTE field paths that are visible iff `dte_involvement === true` (R5.3,
 * R5.5).
 */
const DTE_FIELD_PATHS = Object.freeze([
    'dte.dependencyFunction',
    'dte.dependencyDescription',
    'dte.ownerUserId',
    'dte.dueDate',
]);
/**
 * The CSC field paths that are visible iff `csc_involvement === true` (R5.3,
 * R5.5).
 */
const CSC_FIELD_PATHS = Object.freeze([
    'csc.attributeIds',
    'csc.dependencyFunction',
    'csc.dependencyDescription',
    'csc.ownerUserId',
    'csc.dueDate',
]);
/**
 * Derive the field visibility map from the snapshot's involvement flags.
 *
 * FF fields are visible iff `fieldForceInvolvement === true`; DTE fields iff
 * `dteInvolvement === true`; CSC fields iff `cscInvolvement === true`. Everything
 * else is always visible (R5.2, R5.3, R5.4, R5.5).
 *
 * The map is consumed by both the form renderer (which sections to display) and
 * the validation engine (which fields to exclude from validation), so the two
 * cannot disagree about a field's visibility.
 */
export function deriveFieldVisibility(snapshot) {
    const { fieldForceInvolvement, dteInvolvement, cscInvolvement } = snapshot.initiative;
    const map = new Map();
    // Field Force fields visible iff FF involvement is true
    for (const path of FIELD_FORCE_FIELD_PATHS) {
        map.set(path, fieldForceInvolvement);
    }
    // DTE fields visible iff DTE involvement is true
    for (const path of DTE_FIELD_PATHS) {
        map.set(path, dteInvolvement);
    }
    // CSC fields visible iff CSC involvement is true
    for (const path of CSC_FIELD_PATHS) {
        map.set(path, cscInvolvement);
    }
    // Everything else is always visible - not in the map means visible
    return map;
}
/**
 * Check whether a field path is visible given the visibility map.
 *
 * A field is visible if:
 * 1. It's not in the map (unconditionally visible fields), or
 * 2. It's in the map and mapped to `true`.
 *
 * This is the predicate the validation engine uses to decide whether to evaluate
 * rules targeting a field.
 */
export function isFieldVisible(fieldPath, visibility) {
    const decision = visibility.get(fieldPath);
    // Not in the map means always visible
    if (decision === undefined) {
        return true;
    }
    return decision;
}
/**
 * Prune hidden fields from a snapshot, returning a partial snapshot with hidden
 * fields set to their "unset" state (R5.10).
 *
 * This is applied at submit time rather than on toggle, so cancelling a discard
 * confirmation is a no-op. Hidden fields are set to:
 * - `null` for optional fields
 * - `[]` for collections
 * - `false` for booleans
 *
 * The returned snapshot is partial because it only contains the fields that might
 * need pruning; the caller merges it with the full snapshot.
 */
export function pruneHiddenFields(snapshot) {
    const pruned = {};
    const { fieldForceInvolvement, dteInvolvement, cscInvolvement, dependencies, fieldForceScope } = snapshot.initiative;
    // If Field Force is not involved, prune FF scope
    if (!fieldForceInvolvement && fieldForceScope.length > 0) {
        pruned.fieldForceScope = [];
    }
    // If DTE is not involved, prune DTE dependencies
    if (!dteInvolvement) {
        const prunedDeps = dependencies.filter((dep) => dep.dependencyFunction !== 'dte');
        if (prunedDeps.length !== dependencies.length) {
            pruned.dependencies = prunedDeps;
        }
    }
    // If CSC is not involved, prune CSC dependencies
    if (!cscInvolvement) {
        const existingDeps = (pruned.dependencies ?? dependencies);
        const prunedDeps = existingDeps.filter((dep) => dep.dependencyFunction !== 'csc');
        if (prunedDeps.length !== existingDeps.length) {
            pruned.dependencies = prunedDeps;
        }
        // ...and the CSC attribute classifications.
        if ((snapshot.initiative.cscAttributeIds ?? []).length > 0) {
            pruned.cscAttributeIds = [];
        }
    }
    return pruned;
}
//# sourceMappingURL=field-visibility.js.map